import api from './core';
import { Site, SiteStats, Address } from './types';
import { queryClient } from '@/lib/queryClient';
import { getAddresses } from './addresses';

// Safe version of Site for client-side storage, without sensitive or expiring data
export interface ClientSite {
  id: number; // Always stored as number for consistency
  name: string;
  description?: string;
}

// Utility function to convert Site to ClientSite by removing sensitive/expiring data
export const toClientSite = (site: Site): ClientSite => {
  const { portal_logo, ...safeSiteData } = site;
  
  // Only include essential fields that don't expire
  // Ensure ID is always stored as number for consistency
  return {
    id: Number(safeSiteData.id),
    name: safeSiteData.name,
    description: safeSiteData.description,
  };
};

export const getSites = async (signal?: AbortSignal): Promise<Site[]> => {
  const response = await api.get('/crm/sites/', { signal });
  return response.data;
};

// Get site stats from the site-specific stats endpoint
export const getSiteStats = async (
  siteId: string,
  signal?: AbortSignal
): Promise<SiteStats> => {
  // Ensure we're working with the same site ID in header and URL
  const activeSite = localStorage.getItem('active_site');
  let activeSiteId = siteId;
  
  // If we have an active site, make sure we're using its ID
  if (activeSite) {
    try {
      const parsed = JSON.parse(activeSite);
      // Update active_site in localStorage if the siteId is different
      if (parsed.id !== siteId) {
        const updatedSite = { ...parsed, id: siteId };
        localStorage.setItem('active_site', JSON.stringify(updatedSite));
      }
      activeSiteId = siteId; // Use the passed siteId
    } catch (error) {
      console.error('Failed to parse active site from localStorage', error);
    }
  }
  
  // Make the API call with the updated active site in local storage
  const response = await api.get(`/crm/sites/${activeSiteId}/stats/`, { signal });
  const baseStats = response.data;
  
  // Fetch addresses to calculate square meter breakdown
  try {
    const addresses = await getAddresses({ site_id: activeSiteId }, signal);
    
    // Calculate square meter breakdown from addresses
    let totalFloorSquareMeters = 0;
    let totalRoomSquareMeters = 0;
    const squareMetersByAddress = addresses
      .filter(address => address.square_meters?.floor_total && address.square_meters?.room_total)
      .map(address => {
        const squareMeters = address.square_meters!;
        totalFloorSquareMeters += squareMeters.floor_total;
        totalRoomSquareMeters += squareMeters.room_total;
        
        return {
          address_id: address.id,
          address_name: address.name,
          floor_total: squareMeters.floor_total,
          room_total: squareMeters.room_total,
          breakdown: squareMeters.breakdown
        };
      });
    
    // Add square meter data to the base stats
    return {
      ...baseStats,
      total_floor_square_meters: totalFloorSquareMeters,
      total_room_square_meters: totalRoomSquareMeters,
      square_meters_by_address: squareMetersByAddress
    };
  } catch (error) {
    console.error('Failed to fetch addresses for square meter calculation:', error);
    // Return base stats without square meter data if addresses fetch fails
    return baseStats;
  }
};

export const getSite = async (
  id: string,
  signal?: AbortSignal
): Promise<Site> => {
  const response = await api.get(`/crm/sites/${id}/`, { signal });
  console.log('API response for site:', id, response.data);
  return response.data;
};

export const createSite = async (data: FormData | Partial<Site>): Promise<Site> => {
  // For FormData submissions, we'll use multipart/form-data content type
  if (data instanceof FormData) {
    const response = await api.post('/crm/sites/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    // Invalidate sites cache
    queryClient.invalidateQueries({ queryKey: ['sites'] });
    return response.data;
  }
  
  // For regular JSON submissions, we'll use application/json content type
  console.log('Creating site with data:', data);
  const response = await api.post('/crm/sites/', data);
  // Invalidate sites cache
  queryClient.invalidateQueries({ queryKey: ['sites'] });
  return response.data;
};

export const updateSite = async (id: string, data: FormData | Partial<Site>): Promise<Site> => {
  let response;
  
  // Log the data being sent
  console.log('Updating site with ID:', id);
  
  // For FormData submissions (no longer used with base64)
  if (data instanceof FormData) {
    console.log('FormData keys:', Array.from(data.keys()));
    
    response = await api.patch(`/crm/sites/${id}/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  } else {
    // Check if we have a base64 logo
    if (data.portal_logo && typeof data.portal_logo === 'string' && 
        data.portal_logo.startsWith('data:')) {
      console.log('Sending update with base64 encoded logo');
    } else {
      console.log('Update data without logo:', data);
    }
    
    response = await api.patch(`/crm/sites/${id}/`, data);
  }
  
  // Log the response for debugging
  console.log('Site update response:', response.status, response.data);
  
  // Invalidate related queries immediately
  queryClient.invalidateQueries({ queryKey: ['sites'] });
  queryClient.invalidateQueries({ queryKey: ['site', id] });
  
  return response.data;
};

export const deleteSite = async (id: string): Promise<string> => {
  await api.delete(`/crm/sites/${id}/`);
  // Invalidate sites cache
  queryClient.invalidateQueries({ queryKey: ['sites'] });
  return id; // Return the ID for cache updates
};

export const setActiveSite = async (siteId: string): Promise<void> => {
  await api.put(`/crm/sites/active/${siteId}/`);
};
